import React, { useState } from 'react';
import { cn } from '@/utils/cn';
import { features, cartLabels } from '@/data/content';
import { getAvailableDate } from '@/data/foods';
import AllergenBadges from '@/components/ui/AllergenBadges';
import Button from '@/components/ui/Button';
import AddToBasket from '@/components/features/AddToBasket';
import AvailableDateBadge from '@/components/ui/AvailableDateBadge';
import AddItemWithDatePopup from '@/components/features/AddItemWithDatePopup';
interface FoodInfo {
    id?: string | number;
    item_name?: string;
    title: string;
    price?: string | number;
    rating?: string | number;
    reviewsCount?: string | number;
    description?: string;
    ingredients?: string[];
    allergens?: string[];
    preparation_days?: number;
    weight_percentage?: number;
    daily_capacity?: number;
    portion_quantity?: number;
    portion_label?: string;
    food_unit?: string;
}

interface FoodInfoBoxProps {
    food: FoodInfo;
    compact?: boolean;
}

const FoodInfoBox: React.FC<FoodInfoBoxProps> = ({ food, compact = false }) => {
    const [activeTab, setActiveTab] = useState('description');

    const tabs = [
        { id: 'description', label: cartLabels.description_tab, icon: '📋' },
        { id: 'allergens', label: cartLabels.allergens_tab, icon: '⚠️' },
    ];

    return (
        <div className="food-info-panel">
            {/* Header: Title + Badges */}
            <div className={cn('food-info-header', compact && 'compact')}>
                <div className="food-title-section">
                    <h1 className="food-info-title">{food.title}</h1>
                    <div className="food-badges">
                        <span className="food-badge badge-popular">{cartLabels.popular_badge}</span>
                        <span className="food-badge badge-house">{cartLabels.house_made_badge}</span>
                    </div>
                </div>
                {!compact && (
                    <div className="food-price-section">
                        <span className="food-info-price">{food.price != null ? `${food.price} €` : ''}</span>
                        <span className="food-price-unit">{cartLabels.per_portion}</span>
                        {food.weight_percentage != null && (
                            <span className="text-xs font-medium text-app-muted bg-app-bg px-2 py-0.5 rounded-full inline-block mt-1">
                                🍳 {food.weight_percentage}%
                            </span>
                        )}
                        <span className="text-xs font-medium text-app-muted bg-app-bg px-2 py-0.5 rounded-full inline-block mt-1 ml-2">
                            📦 {food.daily_capacity ?? 0}
                        </span>
                    </div>
                )}
            </div>

            {/* Rating */}
            {features.reviews !== false && (
                <div className="food-info-rating">
                    <div className="rating-stars">
                        <span className="food-info-star">★</span>
                        <span className="food-info-star">★</span>
                        <span className="food-info-star">★</span>
                        <span className="food-info-star">★</span>
                        <span className="food-info-star star-half">★</span>
                    </div>
                    <span className="food-info-rating-num">{food.rating || '4.8'}</span>
                    <span className="food-info-rating-count">
                        ({food.reviewsCount || '124'} {cartLabels.reviews_label})
                    </span>
                </div>
            )}

            {/* Tabs */}
            <div className={cn('food-tabs', compact && 'compact')}>
                <div className="food-tabs-nav">
                    {tabs.map(({ id, label, icon }) => (
                        <Button
                            key={id}
                            variant="ghost"
                            className={cn('food-tab-btn !p-0', activeTab === id && 'is-active')}
                            onClick={() => setActiveTab(id)}
                        >
                            <span className="tab-icon">{icon}</span>
                            <span className="tab-label">{label}</span>
                        </Button>
                    ))}
                </div>

                <div className="food-tab-panel">
                    {activeTab === 'description' && (
                        <div className="tab-content">
                            <p className="food-info-desc">{food.description}</p>
                        </div>
                    )}
                    {activeTab === 'allergens' && (
                        <div className="tab-content">
                            {food.allergens && food.allergens.length > 0 ? (
                                <AllergenBadges allergens={food.allergens} />
                            ) : (
                                <p className="text-app-muted">{cartLabels.no_allergens}</p>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Cart Section */}
            <div className="food-cart-section flex items-center justify-between gap-4 mt-6">
                <div className="flex flex-col gap-1">
                    {food.preparation_days != null && (
                        <AvailableDateBadge date={getAvailableDate(food.preparation_days)!} />
                    )}
                    {(food.portion_label || food.portion_quantity) && (
                        <span className="text-xs font-bold text-gray-500 dark:text-gray-400">
                            Portion: {food.portion_label || `${food.portion_quantity} ${food.food_unit || 'pcs'}`}
                        </span>
                    )}
                </div>
                <AddItemWithDatePopup
                    item={{ id: Number(food.id), type: 'food' }}
                    price={Number(food.price || 0)}
                    isAvailable={true}
                />
            </div>
        </div>
    );
};

export default FoodInfoBox;
